import React, { useState, useEffect } from "react";

import { Box } from "@mui/material";
import { useSearchParams } from "react-router-dom";

import { Camera } from "./Camera";
import { Guidelines } from "./Guidelines";
import { Error } from "./Error";

import { scanPushAnswers } from "../../api/endpoints";
import { setScanContext, trackScanEvent } from "../../telemetry";

const STEPS = {
  GUIDELINES: "Guidelines",
  CAMERA: "Camera",
  ERROR: "Error"
}

export const Scenarii = () => {
  const [step, setStep] = useState(STEPS.GUIDELINES)
  const [searchParams, setSearchParams] = useSearchParams()

  const isValidUrl = (url) => {
    if (typeof url !== 'string' || url.length === 0) return false;
    try {
      const u = new URL(url);
      return (u.protocol === 'http:' || u.protocol === 'https:') && u.hostname.length > 0;
    } catch {
      return false;
    }
  };

  const createPUTLinks = async (mid) => {
    if (!mid) {
      console.warn("createPUTLinks called without mid, skipping (stale URLs kept)");
      return;
    }
    const answers = {
      age: Number(searchParams.get("age")) || 40,
      weight: Number(searchParams.get("weight")) || 70,
      height: Number(searchParams.get("height")) || 170,
      gender: searchParams.get("gender") || 'male',
    }
    await scanPushAnswers(mid, answers).then((links) => {
      // Presigned URLs carry auth signatures: log presence only (console is captured by telemetry)
      console.log('scanPushAnswers links received', {
        hasFront: isValidUrl(links?.front_put_url),
        hasSide: isValidUrl(links?.side_put_url),
      })
      if (links && typeof links.error !== 'string' && isValidUrl(links.front_put_url) && isValidUrl(links.side_put_url)) {
        localStorage.setItem('front_put_url', links.front_put_url)
        localStorage.setItem('side_put_url', links.side_put_url)
        trackScanEvent('scan-put-links-created', { mid })
      } else {
        console.error('scanPushAnswers returned invalid links, clearing stale PUT URLs')
        localStorage.removeItem('front_put_url')
        localStorage.removeItem('side_put_url')
        // Do not log the links themselves: presigned URLs carry auth signatures
        trackScanEvent('scan-put-links-invalid', {
          mid,
          hasResponse: !!links,
          error: typeof links?.error === 'string' ? links.error : undefined,
        })
      }
    })
  };

  const restart = () => {
    const mid = searchParams.get("mid");
    trackScanEvent('scan-restarted', { mid })
    setStep(STEPS.GUIDELINES);
    createPUTLinks(mid);
  }

  useEffect(() => {
    const mid = searchParams.get("mid");
    if (!mid) {
      console.warn("No MID yet, retrying...");
      const retry = setTimeout(() => {
        const retryMid = searchParams.get("mid");
        if (retryMid) createPUTLinks(retryMid);
      }, 500);
      return () => clearTimeout(retry);
    }

    console.log("✅ Creating PUT links with mid:", mid);
    setScanContext({
      mid,
      gender: searchParams.get("gender") || 'male',
    });
    trackScanEvent('scan-session-started', { mid });
    createPUTLinks(mid);
  }, [searchParams]);


  const nextStep = () => {
    if (step === STEPS.GUIDELINES) {
      setStep(STEPS.CAMERA)
    }
  }

  const previousStep = () => {
    if (step === STEPS.CAMERA) {
      setStep(STEPS.GUIDELINES)
    }
  }

  return (
    <Box>
      {step === STEPS.GUIDELINES && (
        <Guidelines
          nextStep={nextStep}
        />
      )}
      {step === STEPS.CAMERA && (
        <Camera
          closeCamera={nextStep}
          previousScreen={previousStep}
          restart={restart}
        />
      )}
      {step === STEPS.ERROR && (
        <Error
          retry={restart}
        />
      )}
    </Box>
  );
};
